import type { AssessmentOverview, PrerequisiteGate } from '@assessments/shared'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { OpenRowButton } from './OpenRowButton.js'
import { SectionRow } from './SectionRow.js'

const bucketHeading = 'mb-3 mt-8 text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle'

function PrerequisiteRow({
  gate,
  canEdit,
  onOpen,
}: {
  gate: PrerequisiteGate
  canEdit: boolean
  onOpen?: (gate: PrerequisiteGate) => void
}) {
  const isSection = gate.sectionId !== null
  const canOpen = gate.enterable && onOpen !== undefined
  // Consenting is an editor's action and gets the primary button. Every other row —
  // an accepted gate, a read-only viewer — opens for review through the same chevron
  // a section row uses.
  const showAcceptCta = canEdit && canOpen && !isSection && !gate.complete
  return (
    <Card
      onClick={canOpen ? () => onOpen?.(gate) : undefined}
      className={cn(
        'flex items-center justify-between gap-4 px-6 py-5',
        canOpen && 'cursor-pointer hover:border-strong',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-jakarta text-[17px] font-bold text-heading">{gate.title}</span>
          {/* Text, not colour, so the system-level distinction survives high-contrast
              modes and screen readers. */}
          {isSection && <Badge>Required for every assessment</Badge>}
        </div>
        {isSection && (
          <div className="mt-1.5 text-[13px] text-subtle">
            {gate.answeredCount} of {gate.questionCount} answered
          </div>
        )}
      </div>
      {showAcceptCta ? (
        <Button
          className="px-4 text-[15px]"
          onClick={(e) => {
            e.stopPropagation()
            onOpen?.(gate)
          }}
        >
          Review &amp; accept
        </Button>
      ) : (
        <div className="flex shrink-0 items-center gap-4">
          <Badge tone={gate.complete ? 'success' : 'neutral'}>
            {gate.complete ? 'Complete' : 'Required'}
          </Badge>
          <OpenRowButton
            label={gate.complete ? 'Review' : canEdit ? 'Open' : 'View'}
            enabled={canOpen}
            onClick={() => onOpen?.(gate)}
          />
        </div>
      )}
    </Card>
  )
}

export interface SectionBucketListProps {
  overview: Pick<AssessmentOverview, 'prerequisites' | 'requiredSections' | 'optionalSections'>
  canEdit: boolean
  onOpenSection?: (sectionId: string) => void
  /** Open a prerequisite — its accept screen, or its section when it has one. */
  onOpenPrerequisite?: (gate: PrerequisiteGate) => void
}

export function SectionBucketList({
  overview,
  canEdit,
  onOpenSection,
  onOpenPrerequisite,
}: SectionBucketListProps) {
  const { prerequisites, requiredSections, optionalSections } = overview
  return (
    <div>
      {prerequisites.length > 0 && (
        <section>
          <h2 className={bucketHeading}>Prerequisites</h2>
          <div className="flex flex-col gap-4">
            {prerequisites.map((g) => (
              <PrerequisiteRow
                key={g.kind}
                gate={g}
                canEdit={canEdit}
                onOpen={onOpenPrerequisite}
              />
            ))}
          </div>
        </section>
      )}

      {requiredSections.length > 0 && (
        <section>
          <h2 className={bucketHeading}>Required</h2>
          <div className="flex flex-col gap-4">
            {requiredSections.map((s) => (
              <SectionRow key={s.id} section={s} canEdit={canEdit} onOpen={onOpenSection} />
            ))}
          </div>
        </section>
      )}

      {optionalSections.length > 0 && (
        <section>
          <h2 className={bucketHeading}>Optional</h2>
          <div className="flex flex-col gap-4">
            {optionalSections.map((s) => (
              <SectionRow key={s.id} section={s} canEdit={canEdit} onOpen={onOpenSection} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
